import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getSessionUser } from '../../services/userService';
import '../../cms.css';
import { useSeo } from '../../hooks/useSeo';
import { Loader2, ArrowLeft, User, Mail, Phone, Home, Package, CreditCard, ShoppingCart, ChevronDown, Plus, X } from 'lucide-react';
import { formatPrice } from '../../utils/formatters';

interface OrderItem {
  product_id: number;
  product_name: string;
  quantity: number;
  price: number;
}

interface OrderDetails {
  order_number: string;
  customer_email: string;
  customer_data: any;
  total_amount: number;
  status: string;
  created_at: number;
  items: OrderItem[];
}

const parseStatus = (statusString: string | null) => {
    if (!statusString) return { name: 'UNKNOWN', color: '#888888' };
    try {
        const parsed = JSON.parse(statusString);
        const name = Object.keys(parsed)[0] || 'UNKNOWN';
        const color = parsed[name] || '#888888';
        return { name, color };
    } catch (e) {
        return { name: statusString, color: '#ff0000' };
    }
};

const OrderDetail: React.FC = () => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [user] = useState(getSessionUser);
  
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isAddingNewStatus, setIsAddingNewStatus] = useState(false);
  const [newStatusName, setNewStatusName] = useState('');
  const [newStatusColor, setNewStatusColor] = useState('#888888');

  const adminPath = location.pathname.split('/')[1];
  
  useSeo({ title: `CMS - Order #${orderNumber} - esoteric.pl` });

  const statusRef = useRef<HTMLDivElement>(null);
  const newStatusRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user || user.rank !== 0) {
      navigate('/404');
      return;
    }

    const fetchOrder = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/shop/orders?number=${orderNumber}`, {
          headers: { 'x-admin-phone': user.phone }
        });
        if (res.ok) {
          const data = await res.json();
          setOrder(data);
        } else {
          console.error("Failed to fetch order details");
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    const fetchStatuses = async () => {
        try {
            const res = await fetch(`/api/shop/orders?distinct=status`, { headers: { 'x-admin-phone': user.phone }});
            if (res.ok) {
                setAvailableStatuses(await res.json());
            }
        } catch(e) { console.error("Could not fetch statuses", e); }
    };
    
    fetchOrder();
    fetchStatuses();

    function handleClickOutside(event: MouseEvent) {
      if (statusRef.current && !statusRef.current.contains(event.target as Node)) {
        setIsStatusDropdownOpen(false);
      }
      if (newStatusRef.current && !newStatusRef.current.contains(event.target as Node)) {
        setIsAddingNewStatus(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [orderNumber, navigate, user]);
  
  const handleStatusChange = async (newStatus: string) => {
    if (!orderNumber || !user) return;
    
    try {
        const res = await fetch('/api/shop/orders', {
            method: 'PUT',
            headers: { 
                'Content-Type': 'application/json',
                'x-admin-phone': user.phone 
            },
            body: JSON.stringify({ order_number: orderNumber, status: newStatus })
        });

        if (res.ok) {
            setOrder(prev => prev ? { ...prev, status: newStatus } : null);
            if (!availableStatuses.includes(newStatus)) {
                setAvailableStatuses(prev => [...prev, newStatus]);
            }
        } else {
            alert("Failed to update status.");
        }
    } catch (e) {
        alert("Error updating status.");
        console.error(e);
    } finally {
        setIsStatusDropdownOpen(false);
        setIsAddingNewStatus(false);
        setNewStatusName('');
        setNewStatusColor('#888888');
    }
  };

  const handleAddNewStatus = () => {
      if (!newStatusName.trim()) {
          alert("Status name cannot be empty.");
          return;
      }
      const newStatusObject = { [newStatusName.toUpperCase()]: newStatusColor };
      handleStatusChange(JSON.stringify(newStatusObject));
  };
  
  const formatDate = (ts: number | null) => ts ? new Date(ts).toLocaleString('pl-PL') : 'Brak danych';

  if (loading) return <div className="min-h-screen bg-mystic-bg flex items-center justify-center"><Loader2 className="animate-spin text-white w-10 h-10" /></div>;
  if (!order) return <div className="min-h-screen bg-mystic-bg text-white text-center p-8">Order not found.</div>;

  const customer = order.customer_data || {};
  const currentStatus = parseStatus(order.status);

  return (
    <div className="min-h-screen bg-mystic-bg text-[#e3e3e3] font-mono text-sm">
        <header className="border-b border-gray-800 bg-mystic-box px-6 h-14 flex items-center justify-between sticky top-0 z-50">
            <div className="flex items-center gap-4">
                <button onClick={() => navigate(`/${adminPath}/orders`)} className="p-2 hover:bg-gray-800 rounded-full text-gray-400 hover:text-white transition-colors">
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <h1 className="text-lg font-bold text-white uppercase tracking-wider">
                    Order <span className="text-mystic-accent">{order.order_number}</span>
                </h1>
            </div>
            <div className="flex items-center gap-4">
                <span className="text-xs text-gray-500">{formatDate(order.created_at)}</span>
                
                <div className="relative" ref={statusRef}>
                    <button 
                        onClick={() => setIsStatusDropdownOpen(prev => !prev)}
                        className="px-3 py-1 rounded-full border font-bold text-xs flex items-center gap-1.5 transition-all hover:opacity-80"
                        style={{
                            backgroundColor: `${currentStatus.color}20`,
                            color: currentStatus.color,
                            borderColor: `${currentStatus.color}80`,
                        }}
                    >
                        {currentStatus.name}
                        <ChevronDown className="w-3 h-3" />
                    </button>
                    {isStatusDropdownOpen && (
                        <div className="absolute top-full right-0 mt-2 w-48 bg-mystic-btn border border-gray-700 rounded-md shadow-lg z-10 p-1 animate-fade-in-up">
                            {availableStatuses.filter(s => s !== order.status).map(statusStr => {
                                const { name, color } = parseStatus(statusStr);
                                return (
                                    <button 
                                        key={statusStr} 
                                        onClick={() => handleStatusChange(statusStr)}
                                        className="w-full text-left px-3 py-1.5 text-xs rounded hover:bg-mystic-accent hover:text-black flex items-center gap-2"
                                        style={{ color: 'white' }}
                                        onMouseOver={e => e.currentTarget.style.color = 'black'}
                                        onMouseOut={e => e.currentTarget.style.color = 'white'}
                                    >
                                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }}></div>
                                        {name}
                                    </button>
                                )
                            })}
                            <div className="h-px bg-gray-700 my-1"></div>
                            <button 
                                onClick={() => { setIsAddingNewStatus(true); setIsStatusDropdownOpen(false); }}
                                className="w-full text-left px-3 py-1.5 text-xs rounded text-gray-400 hover:bg-gray-700 hover:text-white flex items-center gap-2"
                            >
                                <Plus className="w-3 h-3"/> Utwórz nowy status
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>

        <main className="p-6 grid grid-cols-3 gap-6">
            {/* Left Column - Items */}
            <div className="col-span-2 space-y-4">
                 <h2 className="text-gray-500 text-xs uppercase font-bold tracking-widest flex items-center gap-2"><ShoppingCart className="w-4 h-4" /> Items</h2>
                 <div className="bg-mystic-box border border-gray-800 rounded-lg overflow-hidden">
                     <table className="w-full text-left">
                         <thead className="bg-mystic-inset text-gray-500 text-[10px] uppercase tracking-wider">
                             <tr>
                                 <th className="p-3">Product</th>
                                 <th className="p-3 text-center">Qty</th>
                                 <th className="p-3 text-right">Unit Price</th>
                                 <th className="p-3 text-right">Total</th>
                             </tr>
                         </thead>
                         <tbody>
                             {order.items.map((item, idx) => (
                                 <tr key={idx} className="border-t border-gray-800">
                                     <td className="p-3 font-bold text-white">{item.product_name} <span className="text-gray-600 font-normal">#SKU-{item.product_id}</span></td>
                                     <td className="p-3 text-center text-gray-400">{item.quantity}</td>
                                     <td className="p-3 text-right text-gray-400">{formatPrice(item.price)} zł</td>
                                     <td className="p-3 text-right text-white font-bold">{formatPrice(item.price * item.quantity)} zł</td>
                                 </tr>
                             ))}
                         </tbody>
                         <tfoot className="bg-mystic-inset">
                            <tr>
                                <td colSpan={3} className="p-4 text-right font-bold uppercase text-gray-400">Total</td>
                                <td className="p-4 text-right text-xl font-bold text-mystic-accent">{formatPrice(order.total_amount)} zł</td>
                            </tr>
                         </tfoot>
                     </table>
                 </div>
            </div>

            {/* Right Column - Details */}
            <div className="col-span-1 space-y-6">
                 {/* Customer Details */}
                 <div>
                    <h2 className="text-gray-500 text-xs uppercase font-bold tracking-widest mb-2 flex items-center gap-2"><User className="w-4 h-4" /> Customer</h2>
                    <div className="bg-mystic-box border border-gray-800 rounded-lg p-4 space-y-3 text-xs">
                        <div className="flex items-center gap-3"><User className="w-4 h-4 text-gray-600" /><span className="font-bold text-white">{customer.name}</span></div>
                        <div className="flex items-center gap-3"><Mail className="w-4 h-4 text-gray-600" /><span>{customer.email}</span></div>
                        <div className="flex items-center gap-3"><Phone className="w-4 h-4 text-gray-600" /><span>{customer.phone}</span></div>
                    </div>
                 </div>

                 {/* Shipping Details */}
                 <div>
                    <h2 className="text-gray-500 text-xs uppercase font-bold tracking-widest mb-2 flex items-center gap-2"><Package className="w-4 h-4" /> Shipping</h2>
                    <div className="bg-mystic-box border border-gray-800 rounded-lg p-4 space-y-3 text-xs">
                        <p><strong className="text-gray-500 mr-2">Method:</strong> <span className="font-bold text-white">{customer.shippingType}</span></p>
                        {customer.shippingType === 'courier' && (
                            <div className="flex items-start gap-3 border-t border-gray-800 pt-3 mt-3">
                                <Home className="w-4 h-4 text-gray-600 mt-0.5 shrink-0" />
                                <div>
                                    <p>{customer.address}</p>
                                    <p>{customer.zip} {customer.city}</p>
                                </div>
                            </div>
                        )}
                        {customer.shippingType === 'pickup' && (
                            <div className="border-t border-gray-800 pt-3 mt-3 space-y-1">
                                <p><strong className="text-gray-500 mr-2">Point ID:</strong> {customer.inpostPointId}</p>
                                <p><strong className="text-gray-500 mr-2">Address:</strong> {customer.inpostPointAddress}</p>
                                {customer.inpostPointDesc && <p><strong className="text-gray-500 mr-2">Desc:</strong> {customer.inpostPointDesc}</p>}
                            </div>
                        )}
                    </div>
                 </div>

                 {/* Payment Details */}
                 <div>
                    <h2 className="text-gray-500 text-xs uppercase font-bold tracking-widest mb-2 flex items-center gap-2"><CreditCard className="w-4 h-4" /> Payment</h2>
                    <div className="bg-mystic-box border border-gray-800 rounded-lg p-4 space-y-3 text-xs">
                        <p><strong className="text-gray-500 mr-2">Method:</strong> <span className="font-bold text-white">{customer.paymentMethod}</span></p>
                        {customer.paymentMethod === 'blik' && customer.blikCode && (
                            <p><strong className="text-gray-500 mr-2">BLIK Code:</strong> {customer.blikCode}</p>
                        )}
                        {customer.paymentMethod === 'transfer' && customer.bankCode && (
                            <p><strong className="text-gray-500 mr-2">Bank:</strong> {customer.bankCode}</p>
                        )}
                    </div>
                 </div>
            </div>
        </main>

        {isAddingNewStatus && (
            <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center animate-fade-in">
                <div ref={newStatusRef} className="bg-mystic-bg border border-gray-700 p-6 rounded-lg shadow-2xl w-full max-w-sm space-y-4">
                    <div className="flex justify-between items-center">
                        <h4 className="font-bold text-white">Create New Status</h4>
                        <button onClick={() => setIsAddingNewStatus(false)}><X className="w-4 h-4 text-gray-500 hover:text-white"/></button>
                    </div>
                    <div>
                        <label className="text-xs text-gray-400">Status Name</label>
                        <input 
                            type="text"
                            value={newStatusName}
                            onChange={e => setNewStatusName(e.target.value)}
                            placeholder="e.g. SHIPPED"
                            className="w-full bg-mystic-inset border border-gray-700 rounded p-2 mt-1 text-white uppercase"
                        />
                    </div>
                     <div>
                        <label className="text-xs text-gray-400">Color</label>
                        <div className="flex items-center gap-2 mt-1">
                            <input 
                                type="color"
                                value={newStatusColor}
                                onChange={e => setNewStatusColor(e.target.value)}
                                className="w-8 h-8 p-0 border-none rounded bg-transparent"
                            />
                            <input 
                                type="text"
                                value={newStatusColor}
                                onChange={e => setNewStatusColor(e.target.value)}
                                className="flex-1 bg-mystic-inset border border-gray-700 rounded p-2 text-white font-mono"
                            />
                        </div>
                    </div>
                    <button 
                        onClick={handleAddNewStatus}
                        className="w-full bg-mystic-accent text-black font-bold py-2 rounded hover:bg-mystic-accentHover transition-colors"
                    >
                        Add & Apply
                    </button>
                </div>
            </div>
        )}
    </div>
  );
};

export default OrderDetail;